import { Text, type TextProps } from "react-native";
import { colors, typography } from "../theme/tokens";

type MoonTextVariant =
  | "display"
  | "title"
  | "body"
  | "body-sm"
  | "caption"
  | "label"
  | "section-header"
  | "data";

type MoonTextColor = keyof typeof colors | "accent" | "inherit";

type Props = TextProps & {
  variant?: MoonTextVariant;
  color?: MoonTextColor;
};

const VARIANT_STYLES: Record<
  MoonTextVariant,
  {
    fontFamily: string;
    fontSize: number;
    fontWeight?: string;
    letterSpacing?: number;
  }
> = {
  display: {
    fontFamily: typography.families.serif,
    fontSize: typography.sizes["2xl"],
  },
  title: {
    fontFamily: typography.families.sansBold,
    fontSize: typography.sizes.md,
    fontWeight: "600",
  },
  body: {
    fontFamily: typography.families.sans,
    fontSize: typography.sizes.base,
  },
  "body-sm": {
    fontFamily: typography.families.sans,
    fontSize: typography.sizes.sm,
  },
  caption: {
    fontFamily: typography.families.sansMedium,
    fontSize: typography.sizes.xs,
  },
  label: {
    fontFamily: typography.families.sansMedium,
    fontSize: typography.sizes.sm,
    fontWeight: "500",
  },
  "section-header": {
    fontFamily: typography.families.sansBold,
    fontSize: typography.sizes.sm,
    fontWeight: "600",
    letterSpacing: 1,
  },
  data: {
    fontFamily: typography.families.mono,
    fontSize: typography.sizes.lg,
  },
};

const FLAT_COLORS: Record<string, string> = {
  warm: colors.warm,
  warmDark: colors.warmDark,
  warmLight: colors.warmLight,
  coral: colors.coral,
  teal: colors.teal,
  blue: colors.blue,
  green: colors.green,
  gold: colors.gold,
  text: colors.text,
  textMid: colors.textMid,
  textLight: colors.textLight,
  textFaint: colors.textFaint,
  bg: colors.bg,
  bgEnd: colors.bgEnd,
  border: colors.border,
  surface: colors.surface,
  success: colors.success,
  warning: colors.warning,
  error: colors.error,
  info: colors.info,
};

function resolveColor(
  colorProp: MoonTextColor | undefined,
): string | undefined {
  if (!colorProp || colorProp === "inherit") return undefined;
  if (colorProp === "accent") return colors.warm;
  return FLAT_COLORS[colorProp] ?? colors.text;
}

export function MoonText({
  variant = "body",
  color: colorProp,
  style,
  ...rest
}: Props) {
  const variantStyle = VARIANT_STYLES[variant];
  const textColor = resolveColor(colorProp) ?? colors.text;

  return (
    <Text
      style={[
        {
          fontFamily: variantStyle.fontFamily,
          fontSize: variantStyle.fontSize,
          color: textColor,
          letterSpacing: variantStyle.letterSpacing,
          lineHeight: variantStyle.fontSize * typography.lineHeights.relaxed,
        },
        style,
      ]}
      {...rest}
    />
  );
}
